import { supabase } from '../lib/supabase';

export interface AICitation {
  platform: string;
  citations_count: number;
  pages_count: number;
  last_checked?: string;
}

export interface AICitationsData {
  google: { citations: number; pages: number };
  chatgpt: { citations: number; pages: number };
  perplexity: { citations: number; pages: number };
  gemini: { citations: number; pages: number };
  copilot: { citations: number; pages: number };
  grok: { citations: number; pages: number };
}

/**
 * Fetch AI citations for a prospect from the database
 */
export async function getAICitations(prospectId: string): Promise<AICitationsData | null> {
  try {
    const { data, error } = await supabase
      .from('ai_citations_results')
      .select('platform, citations_count, pages_count, last_checked')
      .eq('prospect_id', prospectId);

    if (error) {
      console.error('Error fetching AI citations:', error);
      return null;
    }

    if (!data || data.length === 0) {
      return null;
    }

    // Transform array to object
    const citations: AICitationsData = {
      google: { citations: 0, pages: 0 },
      chatgpt: { citations: 0, pages: 0 },
      perplexity: { citations: 0, pages: 0 },
      gemini: { citations: 0, pages: 0 },
      copilot: { citations: 0, pages: 0 },
      grok: { citations: 0, pages: 0 },
    };

    data.forEach((item: AICitation) => {
      if (item.platform in citations) {
        citations[item.platform as keyof AICitationsData] = {
          citations: item.citations_count,
          pages: item.pages_count,
        };
      }
    });

    return citations;
  } catch (error) {
    console.error('Error in getAICitations:', error);
    return null;
  }
}

/**
 * Generate AI citations for a prospect using the edge function
 */
export async function generateAICitations(prospectId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.functions.invoke('generate-ai-citations', {
      body: { prospectId },
    });

    if (error) {
      console.error('Error generating AI citations:', error);
      return false;
    }

    console.log('✅ AI citations generated:', data);
    return data?.success || false;
  } catch (error) {
    console.error('Error in generateAICitations:', error);
    return false;
  }
}

/**
 * Update AI citations manually (admin only)
 */
export async function updateAICitation(
  prospectId: string,
  platform: string,
  citationsCount: number,
  pagesCount: number
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('ai_citations_results')
      .upsert({
        prospect_id: prospectId,
        platform,
        citations_count: citationsCount,
        pages_count: pagesCount,
        last_checked: new Date().toISOString(),
      }, {
        onConflict: 'prospect_id,platform',
      });

    if (error) {
      console.error('Error updating AI citation:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in updateAICitation:', error);
    return false;
  }
}

/**
 * Delete all AI citations for a prospect
 */
export async function deleteAICitations(prospectId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('ai_citations_results')
      .delete()
      .eq('prospect_id', prospectId);

    if (error) {
      console.error('Error deleting AI citations:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteAICitations:', error);
    return false;
  }
}

/**
 * Get or generate AI citations
 * First tries to fetch from DB, if not found, generates new ones
 */
export async function getOrGenerateAICitations(prospectId: string): Promise<AICitationsData> {
  // Try to fetch existing citations
  const existingCitations = await getAICitations(prospectId);
  
  if (existingCitations) {
    return existingCitations;
  }

  // No citations found, generate new ones
  console.log('🔄 No AI citations found, generating...');
  const generated = await generateAICitations(prospectId);

  if (generated) {
    // Fetch the newly generated citations
    const newCitations = await getAICitations(prospectId);
    if (newCitations) {
      return newCitations;
    }
  }

  // Fallback to zeros if generation failed
  return {
    google: { citations: 0, pages: 0 },
    chatgpt: { citations: 0, pages: 0 },
    perplexity: { citations: 0, pages: 0 },
    gemini: { citations: 0, pages: 0 },
    copilot: { citations: 0, pages: 0 },
    grok: { citations: 0, pages: 0 },
  };
}
